import React, { useMemo, useState, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertTriangle, ChevronDown, GripVertical, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { IBitPublic, IBitBrandRef } from '@/redux/bit/bit.d';
import { useGetActiveBitsQuery } from '@/redux/bit/bit.api.slice';
import { ILaserOrEndmill } from '@/redux/post/post.d';
import {
  reorderFile,
  renameOperation,
  selectV2,
  setOperationBits,
} from '../v2Upload.slice';
import {
  AutofillState,
  autofillState,
  bitAnswerKey,
  ParserAnswers,
  rpmAnswerKey,
} from '../utils/autofill';
import { operationAnswers } from '../utils/camSeed';
import AutofillMark from './AutofillMark';
import { V2Bit, V2File, V2Operation } from '../v2Upload.d';
import {
  bitKey,
  emptyBit,
  plural,
  validSpindle,
  CONTROLLERS,
} from '../utils/fileHelpers';
import { cutKey, dropBeforeIndex } from '../utils/cutOrder';
import { InsertBarSlot } from './InsertBar';
import BitCatalog from './BitCatalog';
import ControllerLogo from './ControllerLogo';
import { FIELD_LABEL } from './fileCardStyles';
import { PANEL_HEADER, PANEL_SHELL } from './Panel';

const HEAD =
  'text-xs font-tertiary font-semibold uppercase tracking-wider text-muted-foreground';

/** The row and its headings share one template — kept apart, they drifted. */
// Seven columns do not fit a phone; mobile stacks the row into a card.
const GRID =
  'grid grid-cols-[44px_minmax(0,1fr)] gap-2.5 px-3 sm:grid-cols-[28px_44px_minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,2.2fr)_minmax(0,0.7fr)_124px] sm:gap-3 sm:px-4';

/** Cells that get a line of their own once the row is stacked. */
const FULL = 'col-span-2 sm:col-span-1';

/** Redline, Masso, Buildbotics — the order of the columns on Add files, not the
 * order the files happen to be held in. Anything unrecognised comes after. */
function orderedControllers(controllers: string[]): string[] {
  const present = new Set(controllers);
  return [
    ...CONTROLLERS.filter((c) => present.has(c)),
    ...[...present].filter((c) => !CONTROLLERS.includes(c)),
  ];
}

/** A file with the operation it belongs to — the unit a row renders — and the
 * operations that follow its edits. */
interface ToolRow {
  op: V2Operation;
  file: V2File;
  /** The same cut under the other controllers. Edits flow into these ONE way:
   * a project is usually the same cut posted three times, so filling in the
   * first controller fills in the rest, while a per-controller correction made
   * further down stays where it was made. */
  mirrors: V2Operation[];
}

/** Every G-code file as a row, in cut order, dropping operations whose file has
 * since been removed. */
function toolRows(operations: V2Operation[], files: V2File[]): ToolRow[] {
  const paired = operations
    .map((op) => ({ op, file: files.find((f) => f.id === op.fileIds[0]) }))
    .filter((row): row is { op: V2Operation; file: V2File } =>
      Boolean(row.file)
    );

  // The source controller is the first one present, in column order.
  const source = orderedControllers(paired.map((r) => r.file.controller))[0];
  // Not op.name: it keeps its controller, so twins never pair.
  const key = (row: { file: V2File }) => cutKey(row.file);

  return paired.map((row) => ({
    ...row,
    mirrors:
      row.file.controller === source
        ? paired
            .filter(
              (other) =>
                other.file.controller !== source && key(other) === key(row)
            )
            .map((other) => other.op)
        : [],
  }));
}

const rowAssigned = (op: V2Operation): boolean =>
  op.bits.some((bit) => bit.bitId);

/** A laser has no spindle, so its cell says so rather than asking. */
const isLaser = (bit: V2Bit) => bit.laserOrEndmill === ILaserOrEndmill.LASER;

/** Endmill shots are a ~10:1 sliver on a square canvas — contained, they render
 * 2px tall, so crop to the band. Lasers are portrait and must not be cropped. */
function BitThumb({
  src,
  laser,
  className = 'h-9 w-24 sm:w-40',
}: {
  src: string;
  laser?: boolean;
  className?: string;
}) {
  const box = cn(className, 'shrink-0 rounded');
  if (!src) return <span className={cn(box, 'bg-muted')} />;
  return (
    <img
      src={src}
      alt=""
      className={cn(box, laser ? 'object-contain' : 'object-cover')}
    />
  );
}

/** The picked bit's name, or the prompt to pick one. */
function BitLabel({ name }: { name: string }) {
  return (
    <span
      className={cn(
        'min-w-0 flex-1 truncate text-left text-sm',
        !name && 'text-muted-foreground'
      )}
    >
      {name || 'Select bit or laser…'}
    </span>
  );
}

/** The picked bit, or the prompt to pick one. Opens the catalog dialog. */
function BitField({
  bit = emptyBit(),
  invalid,
  readOnly,
  onOpen,
}: {
  bit?: V2Bit;
  invalid?: boolean;
  readOnly?: boolean;
  onOpen: () => void;
}) {
  const face = (
    <>
      <BitThumb src={bit.imageUrl ?? ''} laser={isLaser(bit)} />
      <BitLabel name={bit.bitName} />
    </>
  );
  if (readOnly) {
    return <div className="flex h-12 items-center gap-2">{face}</div>;
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Bit or laser"
      className={cn(
        'flex h-12 w-full items-center gap-2 rounded-lg border px-2 transition-colors duration-fast hover:border-border-strong',
        invalid && 'border-warning'
      )}
    >
      {face}
      <ChevronDown className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

/** The speed as Sign-off and the read-only table show it. */
function ReadOnlySpeed({ speed }: { speed: string }) {
  return (
    <span className="flex h-12 items-center text-sm">
      {speed ? `${speed} RPM` : '—'}
    </span>
  );
}

function SpeedInput({
  bit,
  onChange,
}: {
  bit: V2Bit;
  onChange: (v: string) => void;
}) {
  const wrong = Boolean(bit.bitId) && !validSpindle(bit.spindleSpeed);
  return (
    <div className="relative">
      <Input
        inputMode="numeric"
        value={bit.spindleSpeed}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. 14,000"
        aria-label="Spindle speed"
        className={cn('h-12 pr-11 text-sm', wrong && 'border-warning')}
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-tertiary text-info">
        RPM
      </span>
    </div>
  );
}

/** RPM for one bit. */
function SpeedField({
  bit,
  readOnly,
  onChange,
}: {
  bit?: V2Bit;
  readOnly?: boolean;
  onChange: (v: string) => void;
}) {
  if (!bit) return <ReadOnlySpeed speed="" />;
  if (isLaser(bit)) {
    return <span className="text-sm text-muted-foreground">n/a</span>;
  }
  if (readOnly) return <ReadOnlySpeed speed={bit.spindleSpeed} />;
  return <SpeedInput bit={bit} onChange={onChange} />;
}

/** A catalog pick as the wizard stores it. */
function bitFrom(
  bit: IBitPublic,
  brand?: IBitBrandRef,
  spindleSpeed = ''
): V2Bit {
  const { bitType = '', imageUrl = '' } = bit;
  return {
    ...emptyBit(bitType, spindleSpeed),
    bitId: bit._id,
    brandId: brand?._id ?? '',
    bitName: bit.name,
    laserOrEndmill: bit.laserOrEndmill,
    imageUrl,
  };
}

/** The same bits for a mirrored row, reusing that row's existing bit ids where
 * it has them — a fresh id on every keystroke would remount its speed input. */
function cloneBits(existing: V2Bit[], bits: V2Bit[]): V2Bit[] {
  return bits.map((bit, i) => ({
    ...bit,
    id: existing[i]?.id ?? emptyBit().id,
  }));
}

/** A bit still missing the speed it has to be saved with. A laser has no
 * spindle, so it never is. */
function needsSpeed(bit: V2Bit): boolean {
  return (
    bit.laserOrEndmill !== ILaserOrEndmill.LASER &&
    !validSpindle(bit.spindleSpeed)
  );
}

/** The speed field for one bit in the selection — or the note that a laser has
 * no spindle to set. */
function DraftSpeed({
  bit,
  onSpeed,
}: {
  bit: V2Bit;
  onSpeed: (bitRowId: string, speed: string) => void;
}) {
  if (isLaser(bit)) {
    return (
      <span className="w-[150px] text-sm text-muted-foreground">
        no spindle speed
      </span>
    );
  }
  const missing = needsSpeed(bit);
  return (
    <div className="flex shrink-0 items-center gap-2.5">
      <span className={cn(FIELD_LABEL, missing && 'text-warning')}>
        Spindle speed
      </span>
      <div className="relative">
        <Input
          inputMode="numeric"
          value={bit.spindleSpeed}
          onChange={(e) => onSpeed(bit.id, e.target.value)}
          placeholder="e.g. 14,000"
          aria-label={`Spindle speed for ${bit.bitName}`}
          className={cn(
            'h-9 w-[150px] pr-11 text-sm',
            missing && 'border-warning'
          )}
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs font-tertiary text-info">
          RPM
        </span>
      </div>
    </div>
  );
}

/** One bit in the selection: its picture, its name, its speed, and the way to
 * take it back out. */
function DraftRow({
  bit,
  onSpeed,
  onRemove,
}: {
  bit: V2Bit;
  onSpeed: (bitRowId: string, speed: string) => void;
  onRemove: (bitRowId: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <BitThumb
        src={bit.imageUrl ?? ''}
        laser={isLaser(bit)}
        className="h-9 w-20 sm:w-28"
      />
      <span className="min-w-0 flex-1 truncate text-sm">{bit.bitName}</span>
      <DraftSpeed bit={bit} onSpeed={onSpeed} />
      <button
        type="button"
        onClick={() => onRemove(bit.id)}
        aria-label={`Remove ${bit.bitName}`}
        className="shrink-0 text-muted-foreground transition-colors hover:text-destructive"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Everything picked so far, or the note that nothing is. */
function DraftList({
  draft,
  onSpeed,
  onRemove,
}: {
  draft: V2Bit[];
  onSpeed: (bitRowId: string, speed: string) => void;
  onRemove: (bitRowId: string) => void;
}) {
  if (draft.length === 0) {
    return <p className="text-xs text-muted-foreground">Nothing picked yet.</p>;
  }
  return (
    <div className="space-y-2">
      {draft.map((bit) => (
        <DraftRow
          key={bit.id}
          bit={bit}
          onSpeed={onSpeed}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
}

/** The save button names what it will write, and where. */
function saveLabel(count: number, groupName: string): string {
  if (count === 0) return `Save to ${groupName}`;
  return `Save ${count} ${plural(count, 'bit', 'bits')} to ${groupName}`;
}

/** Why the save is disabled, when it is. */
function SpeedWarning({
  incomplete,
  total,
}: {
  incomplete: number;
  total: number;
}) {
  if (incomplete === 0) return null;
  const text =
    incomplete === total
      ? 'Enter a spindle speed to save.'
      : `${incomplete} of ${total} still need a spindle speed.`;
  return <p className="text-sm text-warning">{text}</p>;
}

/** What is picked so far, each with the speed it runs at, and the button that
 * writes them to the row. */
function SelectionFooter({
  draft,
  groupName,
  onSpeed,
  onRemove,
  onSave,
}: {
  draft: V2Bit[];
  /** The operation the bits are saved to — named on the button, since the same
   * save also lands on that cut under the other controllers. */
  groupName: string;
  onSpeed: (bitRowId: string, speed: string) => void;
  onRemove: (bitRowId: string) => void;
  onSave: () => void;
}) {
  const incomplete = draft.filter(needsSpeed).length;
  return (
    <div className="mt-4 border-t pt-4">
      <DraftList draft={draft} onSpeed={onSpeed} onRemove={onRemove} />
      <div className="mt-5 flex flex-col items-center gap-2">
        <Button
          size="lg"
          onClick={onSave}
          disabled={draft.length === 0 || incomplete > 0}
          className="min-w-[240px]"
        >
          {saveLabel(draft.length, groupName)}
        </Button>
        <SpeedWarning incomplete={incomplete} total={draft.length} />
      </div>
    </div>
  );
}

interface RowProps {
  row: ToolRow;
  index: number;
  /** What the CAM file says every row's bit and speed are. */
  answers: ParserAnswers;
  readOnly?: boolean;
  invalid?: boolean;
  dragging: string | null;
  onDragStart: (fileId: string) => void;
  onDropOn: (fileId: string) => void;
  /** This section's file ids, so the bar knows which edge to sit on. */
  sectionIds: string[];
  hot: string | null;
  onHot: (opId: string | null) => void;
}

/** The drag handle. Present but invisible on a read-only table, so the columns
 * line up with the editable one. */
function DragHandle({
  readOnly,
  onDragStart,
  focus,
}: {
  readOnly?: boolean;
  onDragStart: () => void;
  focus?: string;
}) {
  return (
    <span
      data-focus={focus}
      draggable={!readOnly}
      onDragStart={onDragStart}
      aria-label="Reorder"
      className={cn(
        'mt-2.5 text-muted-foreground max-sm:hidden',
        readOnly ? 'opacity-0' : 'cursor-grab active:cursor-grabbing'
      )}
    >
      <GripVertical className="h-4 w-4" />
    </span>
  );
}

/** What the cut is called — editable, except on Sign-off's read-only copy. */
function OperationName({
  name,
  readOnly,
  onRename,
}: {
  name: string;
  readOnly?: boolean;
  onRename: (v: string) => void;
}) {
  if (readOnly) {
    return <span className="mt-2 truncate text-sm font-medium">{name}</span>;
  }
  return (
    <Input
      value={name}
      aria-label="Operation"
      onChange={(e) => onRename(e.target.value)}
      className="h-9 border-transparent bg-transparent px-2 text-sm hover:border-border focus:border-border"
    />
  );
}

/** One line per bit: a file cut with three bits reads as three rows inside its
 * own row, rather than hiding all but the first. */
const bitLines = (bits: V2Bit[]): Array<V2Bit | undefined> =>
  bits.length > 0 ? bits : [undefined];

function BitColumn({
  bits,
  assigned,
  readOnly,
  onOpen,
  focus,
  mark,
}: {
  bits: V2Bit[];
  assigned: boolean;
  readOnly?: boolean;
  onOpen: () => void;
  focus?: string;
  /** Only the first line wears it: the CAM seed puts one bit on a row. */
  mark: AutofillState;
}) {
  const warnOn = !assigned && !readOnly ? 0 : -1;
  const markOn = (i: number): AutofillState => (i === 0 ? mark : null);
  return (
    <div className={cn('space-y-2', FULL)} data-focus={focus}>
      {bitLines(bits).map((bit, i) => (
        <div key={bit?.id ?? 'empty'} className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <BitField
              bit={bit}
              invalid={!assigned}
              readOnly={readOnly}
              onOpen={onOpen}
            />
          </div>
          <AutofillMark state={markOn(i)} />
          {i === warnOn && (
            <AlertTriangle className="h-4 w-4 shrink-0 text-warning" />
          )}
        </div>
      ))}
    </div>
  );
}

const BRAND_CELL =
  'flex h-12 items-center truncate text-sm text-muted-foreground';

/** Who sells the picked bit. The row only stores `brandId`, so the name comes
 * from the catalog — the same cached list the picker renders from. */
function BrandColumn({ bits }: { bits: V2Bit[] }) {
  const { data: catalog = [] } = useGetActiveBitsQuery();
  const names = useMemo(() => {
    const byId = new Map<string, string>();
    catalog.forEach((entry) =>
      entry.brands.forEach((brand) => byId.set(brand._id, brand.name))
    );
    return byId;
  }, [catalog]);

  if (bits.length === 0) return <span className={cn(BRAND_CELL, FULL)}>—</span>;

  return (
    <div className={cn('space-y-2', FULL)}>
      {bits.map((bit) => (
        <span key={bit.id} className={BRAND_CELL}>
          {names.get(bit.brandId) || '—'}
        </span>
      ))}
    </div>
  );
}

function SpeedColumn({
  bits,
  readOnly,
  onChange,
  mark,
}: {
  bits: V2Bit[];
  readOnly?: boolean;
  onChange: (i: number, v: string) => void;
  mark: AutofillState;
}) {
  const markOn = (i: number): AutofillState => (i === 0 ? mark : null);
  return (
    <div className={cn('space-y-2', FULL)}>
      {bitLines(bits).map((bit, i) => (
        <div key={bit?.id ?? 'empty'} className="flex items-center gap-1.5">
          <div className="min-w-0 flex-1">
            <SpeedField
              bit={bit}
              readOnly={readOnly}
              onChange={(v) => onChange(i, v)}
            />
          </div>
          <AutofillMark state={markOn(i)} />
        </div>
      ))}
    </div>
  );
}

/** The catalog dialog for one row: pick bits, give each a speed, save. */
function BitPickerDialog({
  open,
  op,
  file,
  draft,
  setDraft,
  onSave,
  onClose,
}: {
  open: boolean;
  op: V2Operation;
  file: V2File;
  draft: V2Bit[];
  setDraft: (fn: (current: V2Bit[]) => V2Bit[]) => void;
  onSave: (bits: V2Bit[]) => void;
  onClose: () => void;
}) {
  const [primary] = op.bits;
  const groupName = op.name || file.name;

  // Nothing leaves this dialog half-answered: a picked bit without its speed
  // would reach the row as an assignment the submit then rejects. Taking the
  // bit back out of the selection is the way past it.
  const incomplete = draft.some(needsSpeed);
  const dismiss = () => !incomplete && onClose();

  const toggle = (bit: IBitPublic, brand?: IBitBrandRef) =>
    setDraft((current) => {
      const key = bitKey(bit._id, brand?._id ?? '');
      const without = current.filter((b) => bitKey(b.bitId, b.brandId) !== key);
      const removed = without.length < current.length;
      return removed ? without : [...current, bitFrom(bit, brand)];
    });

  const setSpeed = (id: string, spindleSpeed: string) =>
    setDraft((cur) =>
      cur.map((b) => (b.id === id ? { ...b, spindleSpeed } : b))
    );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && dismiss()}>
      <DialogContent
        className="max-w-6xl"
        onEscapeKeyDown={(e) => incomplete && e.preventDefault()}
        onInteractOutside={(e) => incomplete && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Bit or laser for {groupName}</DialogTitle>
          <p className="text-xs text-muted-foreground">
            Pick the bits this file was cut with, give each its speed, and save.
          </p>
        </DialogHeader>
        <BitCatalog
          picked={draft.map((b) => bitKey(b.bitId, b.brandId))}
          suggestedType={primary?.bitType}
          onPick={toggle}
        />
        <SelectionFooter
          draft={draft}
          groupName={groupName}
          onSpeed={setSpeed}
          onRemove={(id) => setDraft((cur) => cur.filter((b) => b.id !== id))}
          onSave={() => onSave(draft)}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * The two edits that fan out to the same cut under the other controllers.
 *
 * One way, deliberately: filling in the first controller fills in the rest,
 * while a per-controller correction made further down stays where it was made.
 */
function useMirroredEdits({ op, mirrors }: ToolRow) {
  const dispatch = useDispatch();

  const saveBits = (bits: V2Bit[]) => {
    dispatch(setOperationBits({ opId: op.id, bits }));
    mirrors.forEach((mirror) =>
      dispatch(
        setOperationBits({
          opId: mirror.id,
          bits: cloneBits(mirror.bits, bits),
        })
      )
    );
  };

  const rename = (name: string) => {
    dispatch(renameOperation({ opId: op.id, name }));
    mirrors.forEach((m) => dispatch(renameOperation({ opId: m.id, name })));
  };

  return { saveBits, rename };
}

/** The catalog dialog's own state: which bits are picked so far, and whether
 * it is up at all. A read-only table never opens it. */
function useBitPicker(bits: V2Bit[], readOnly?: boolean) {
  const [showing, setShowing] = useState(false);
  const [draft, setDraft] = useState<V2Bit[]>([]);

  const open = () => {
    if (readOnly) return;
    setDraft(bits.filter((b) => b.bitId));
    setShowing(true);
  };

  return { showing, draft, setDraft, open, close: () => setShowing(false) };
}

/** That one bit's speed, changed; the rest as they were. */
const withSpeedAt = (bits: V2Bit[], at: number, spindleSpeed: string) =>
  bits.map((b, i) => (i === at ? { ...b, spindleSpeed } : b));

/** The two badges a row wears: what the CAM file says its bit and speed are,
 * and whether either has since been changed. */
function rowMarks(op: V2Operation, answers: ParserAnswers) {
  const [bit = emptyBit()] = op.bits;
  return {
    bit: autofillState(answers, [[bitAnswerKey(op.id), bit.bitId]]),
    rpm: autofillState(answers, [[rpmAnswerKey(op.id), bit.spindleSpeed]]),
  };
}

/** The tour points at one example row, and a repeated data-focus would make
 * useFocusField's jumps ambiguous. */
const tourAnchors = (index: number, readOnly?: boolean) =>
  index === 1 && !readOnly
    ? { order: 'cut-order', bit: 'bit' }
    : { order: undefined, bit: undefined };

const rowClass = (held: boolean, invalid?: boolean) =>
  cn(
    'relative border-t first:border-t-0',
    held && 'opacity-50',
    invalid && 'bg-destructive/[0.04]'
  );

/** Only an editable row can ask for a bit it does not have. */
const needsAssignment = (assigned: boolean, readOnly?: boolean) =>
  !assigned && !readOnly;

/** A row is a drop target only while something is being dragged. `onOver` is
 * what lets the insert bar follow the pointer instead of guessing. */
function dropTarget(
  dragging: boolean,
  onDrop: () => void,
  onOver: () => void,
  onLeave: () => void
) {
  if (!dragging) return {};
  return {
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      onOver();
    },
    onDragLeave: onLeave,
    onDrop,
  };
}

function AssignmentNote({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="px-3 pb-2 text-xs text-warning sm:px-4 sm:pl-[92px]">
      Assignment required
    </div>
  );
}

/** One G-code file: its place in the cut order, what the cut is called, and the
 * bit or laser it runs. */
function Row({
  row,
  index,
  answers,
  readOnly,
  invalid,
  dragging,
  onDragStart,
  onDropOn,
  sectionIds,
  hot,
  onHot,
}: RowProps) {
  const { op, file } = row;
  const assigned = rowAssigned(op);
  const { saveBits, rename } = useMirroredEdits(row);
  const picker = useBitPicker(op.bits, readOnly);
  const anchors = tourAnchors(index, readOnly);
  const marks = rowMarks(op, answers);

  const setSpeedAt = (i: number, spindleSpeed: string) =>
    saveBits(withSpeedAt(op.bits, i, spindleSpeed));

  return (
    <div
      data-focus={`op-${op.id}`}
      data-invalid={invalid || undefined}
      {...dropTarget(
        Boolean(dragging),
        () => onDropOn(file.id),
        () => onHot(file.id),
        () => onHot(null)
      )}
      className={rowClass(dragging === file.id, invalid)}
    >
      <InsertBarSlot
        ids={sectionIds}
        dragId={dragging}
        hot={hot}
        index={sectionIds.indexOf(file.id)}
        axis="y"
      />
      <div className={cn(GRID, 'items-start py-2.5')}>
        <DragHandle
          focus={anchors.order}
          readOnly={readOnly}
          onDragStart={() => onDragStart(file.id)}
        />
        <span className="mt-1 flex h-8 w-8 items-center justify-center rounded-lg border text-sm font-semibold">
          {index}
        </span>
        <OperationName name={op.name} readOnly={readOnly} onRename={rename} />
        <span
          title={file.name}
          className="mt-2.5 truncate font-tertiary text-sm text-muted-foreground max-sm:col-start-2 max-sm:mt-0"
        >
          {file.name}
        </span>
        <BitColumn
          focus={anchors.bit}
          bits={op.bits}
          assigned={assigned}
          readOnly={readOnly}
          onOpen={picker.open}
          mark={marks.bit}
        />
        <BrandColumn bits={op.bits} />
        <SpeedColumn
          bits={op.bits}
          readOnly={readOnly}
          onChange={setSpeedAt}
          mark={marks.rpm}
        />
      </div>

      <AssignmentNote show={needsAssignment(assigned, readOnly)} />

      {picker.showing && (
        <BitPickerDialog
          open
          op={op}
          file={file}
          draft={picker.draft}
          setDraft={picker.setDraft}
          onSave={(bits) => {
            saveBits(bits);
            picker.close();
          }}
          onClose={picker.close}
        />
      )}
    </div>
  );
}

/** How many files this controller has, and how far through they are. */
function sectionCount(rows: ToolRow[]): string {
  const assigned = rows.filter((r) => rowAssigned(r.op)).length;
  const waiting = rows.length - assigned;
  const head = `${rows.length} ${plural(rows.length, 'file', 'files')} · ${assigned} assigned`;
  return waiting > 0 ? `${head} · ${waiting} waiting` : head;
}

function ColumnHeadings() {
  return (
    <div className={cn(GRID, 'py-2 max-sm:hidden', HEAD)}>
      <span />
      <span>Order</span>
      <span>Operation</span>
      <span>File Name</span>
      <span>Bit or Laser</span>
      <span>Brand</span>
      <span>Speed</span>
    </div>
  );
}

/** One controller's files, collapsible, with its own assigned/waiting count. */
function ControllerSection({
  controller,
  rows,
  offset,
  defaultOpen,
  forceOpen,
  children,
}: {
  controller: string;
  rows: ToolRow[];
  offset: number;
  /** Only the first section opens on arrival — three tables at once is a wall,
   * and the same cut is usually posted for all three controllers. */
  defaultOpen?: boolean;
  /** Continue is blocked on a row in here; closing it would hide the blocker. */
  forceOpen?: boolean;
  children: (row: ToolRow, index: number) => ReactNode;
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  const shown = open || Boolean(forceOpen);

  return (
    <section className={PANEL_SHELL}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={shown}
        className={cn(PANEL_HEADER, 'w-full gap-3')}
      >
        {/* The mark repeats the name beside it, and its alt text would be read
            out first — "Maso Masso 3 files". */}
        <span aria-hidden>
          <ControllerLogo name={controller} className="h-6 w-6" />
        </span>
        <span className="font-secondary text-sm font-semibold uppercase tracking-wide">
          {controller}
        </span>
        <span className="grow" />
        <span className="text-sm text-muted-foreground">
          {sectionCount(rows)}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-fast',
            shown && 'rotate-180'
          )}
        />
      </button>

      {shown && (
        <div className="border-t">
          <ColumnHeadings />
          {rows.map((row, i) => children(row, offset + i + 1))}
        </div>
      )}
    </section>
  );
}

/**
 * Every G-code file in the project, grouped by the controller it was placed
 * under, in cut order. `readOnly` renders the same rows for Sign-off.
 */
export default function ToolTable({
  operations,
  files,
  readOnly,
  invalidOps = [],
}: {
  operations: V2Operation[];
  files: V2File[];
  readOnly?: boolean;
  /** Operation ids Continue is blocked on. */
  invalidOps?: string[];
}) {
  const dispatch = useDispatch();
  const [dragging, setDragging] = useState<string | null>(null);
  const [hotRow, setHotRow] = useState<string | null>(null);
  const rows = toolRows(operations, files);
  const { camExtracted, basics } = useSelector(selectV2);
  const { data: catalog = [] } = useGetActiveBitsQuery();
  // Once for the table, not once per row: the match is a scan over toolpaths.
  const answers = useMemo(
    () =>
      operationAnswers(
        operations,
        files,
        camExtracted,
        catalog,
        basics.material
      ),
    [operations, files, camExtracted, catalog, basics.material]
  );

  const controllers = orderedControllers(rows.map((r) => r.file.controller));

  return (
    <div className="space-y-4">
      {controllers.map((controller, section) => {
        const own = rows.filter((r) => r.file.controller === controller);
        const sectionIds = own.map((r) => r.file.id);
        return (
          <ControllerSection
            key={controller}
            controller={controller}
            rows={own}
            offset={0}
            defaultOpen={section === 0}
            forceOpen={own.some((r) => invalidOps.includes(r.op.id))}
          >
            {(row, index) => (
              <Row
                key={row.op.id}
                row={row}
                index={index}
                answers={answers}
                readOnly={readOnly}
                invalid={invalidOps.includes(row.op.id)}
                dragging={dragging}
                onDragStart={setDragging}
                sectionIds={sectionIds}
                hot={hotRow}
                onHot={setHotRow}
                onDropOn={(fileId) => {
                  if (dragging) {
                    // Dropping anywhere on a row takes that row's slot;
                    // inserting before it lands a downward drag one short.
                    const beforeFileId = dropBeforeIndex(
                      sectionIds,
                      dragging,
                      sectionIds.indexOf(fileId)
                    );
                    dispatch(
                      beforeFileId
                        ? reorderFile({ fileId: dragging, beforeFileId })
                        : reorderFile({ fileId: dragging, controller })
                    );
                  }
                  setDragging(null);
                  setHotRow(null);
                }}
              />
            )}
          </ControllerSection>
        );
      })}
    </div>
  );
}
